import { Link } from "gatsby"
import ConditionalWrapper from "@/components/ConditionalWrapper"
import * as styles from "@/styles/modules/Logo.module.scss"

interface LogoProps {
  className?: string
  flavor?: "chocolate" | "vanilla"
  linkTo?: string
  onClick?: React.MouseEventHandler
  variant?: "horizontal" | "vertical" | "emblem"
}

const Logo = ({
  className,
  flavor,
  linkTo,
  onClick,
  variant = "horizontal",
}: LogoProps) => {
  let doughFillStyle: React.CSSProperties | undefined
  let letterFillStyle: React.CSSProperties | undefined

  if (flavor === "chocolate") {
    doughFillStyle = { fill: "#6C3C31" }
    letterFillStyle = { fill: "#000" }
  }

  if (flavor === "vanilla") {
    doughFillStyle = { fill: "#EBBC60" }
    letterFillStyle = { fill: "#FFF" }
  }

  const handleClick: React.MouseEventHandler = e => {
    if (onClick) onClick(e)
  }

  const handleKeyDown: React.KeyboardEventHandler = e => {
    if (onClick) {
      if (e.code === "Space" || e.code === "Enter") {
        // @ts-expect-error we don't need to pass the keyboard event into the click handler here
        onClick()
      }
    }
  }

  const renderSwitch = () => {
    switch (variant) {
      case "vertical":
        return (
          <svg
            className={[styles.svg, styles.vertical].join(" ")}
            viewBox="0 0 100 108"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.dough}
              d="M12.46 35.12c.16.1.26.24.29.42.08.52-.45 1.19-.56 1.32a.7.7 0 0 1-.56.25c-.26 0-.54-.12-.76-.3-.45-.36-.58-.85-.32-1.2.4-.54 1.3-.87 1.91-.5Zm-2.02-9.29c.07 0 .15 0 .24-.03l.16-.02c.22-.03.55-.07.63-.45.08-.42.03-1.2-.47-1.48-.3-.18-.63-.13-.9.12-.37.37-.52 1.09-.3 1.5.08.15.26.36.64.36Zm2.97 22.83c-.41-.27-.92-.4-1.46-.4-.45.01-1.93.11-2.13 1.14-.14.73.31 1.76.99 2.26.27.2.56.3.84.3.2 0 .4-.05.59-.15.74-.4 1.98-1.2 1.9-2.22-.03-.33-.27-.64-.73-.93ZM87.75 37.8a37.55 37.55 0 0 1-9.95 25.5 37.75 37.75 0 0 1-40.48 10.02 38.02 38.02 0 0 1-20.95-18.4.58.58 0 0 1 .14-.7 11.41 11.41 0 0 0 3.8-8.75v-.08l.06-.05a12.9 12.9 0 0 0 4.21-12.79 13.04 13.04 0 0 0-5.21-7.63l-.08-.05v-.09c.06-2.12-.48-4.2-1.56-6.03a.58.58 0 0 1 0-.6A38.17 38.17 0 0 1 31.18 5.02 37.76 37.76 0 0 1 87.75 37.8ZM50 49.86a12.28 12.28 0 0 0 11.88-9.75c.48-2.45.19-4.97-.85-7.3a12.05 12.05 0 0 0-4.87-5.49c-1.87-1.1-4-1.64-6.12-1.64-2.8 0-5.58.93-7.78 2.75a12.28 12.28 0 0 0-3.6 13.58A12.17 12.17 0 0 0 50 49.86Zm-33.34-5.1c.5 0 .87-.2 1.1-.54.46-.68.29-1.86 0-2.66-.16-.42-.42-.56-.62-.6-.33-.07-.72.1-1.04.43a2.56 2.56 0 0 0-.67 2.08c.08.63.44 1.3 1.2 1.3h.03Zm-.93-14.67c.02-.37.04-2.2-1.01-2.32-.82-.1-1.5.72-1.75 1.29-.4.87-.35 1.48.14 1.83a2 2 0 0 0 1.12.31c.31 0 .62-.06.87-.18.39-.19.61-.52.63-.93Z"
              fill="#EBBC60"
              style={doughFillStyle}
            />
            <path
              d="M25.63 64.71a36.08 36.08 0 0 0 24.22 9.39c.83-1.21 1.7-2.34 1.7-4.74 0-4.51-3.1-4.51-3.1-9.03 0-4.55 3.16-4.5 3.1-9.14A13.52 13.52 0 0 1 36.57 39.3h-.11c-4.13 0-4.49-2.6-7.98-3.04-.38 4.62-2.7 7.87-5.18 10.26 0 0-.06 5.26-4.76 8.71a.41.41 0 0 0-.11.53 36.5 36.5 0 0 0 7.2 8.94Z"
              fill="#F84FAA"
            />
            <path
              d="M36.48 37.75c0-6.94 5.23-12.66 11.97-13.43v-.1c0-4.52 3.1-4.52 3.1-9.04 0-4.51-3.1-4.51-3.1-9.03 0-2.4.88-3.53 1.7-4.74A36.15 36.15 0 0 0 24.3 12.06a36.65 36.65 0 0 0-4.42 5.35c-.1.15-.1.36.03.5.82.93 2.52 3.17 2.68 5.94 0 0 5.76 4.91 5.94 10.55.02.65 0 1.27-.05 1.87 3.53.44 3.85 3.08 8.09 3.04a12.8 12.8 0 0 1-.1-1.56Z"
              fill="#FFFCE9"
            />
            <path
              d="M75.7 12.06A36.1 36.1 0 0 0 50.15 1.4c-.82 1.21-1.7 2.34-1.7 4.74 0 4.52 3.1 4.52 3.1 9.03 0 4.52-3.1 4.52-3.1 9.03v.11A13.52 13.52 0 0 1 63.43 36.2h.12c4.52 0 4.52 3.2 9.03 3.2 4.52 0 4.52-3.2 9.03-3.2 2.4 0 3.53.88 4.73 1.7v-.15c0-9.7-3.78-18.83-10.64-25.7ZM46.87 11.18a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M48.63 14.67a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM39.92 15.16a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM45.47 7.68a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M43.63 16.97a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M45.95 23.5a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M48.1 19.03a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M44.48 20.42a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M36.72 12.06a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M41.86 10.1a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M32.82 12.6a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M29.4 14.67a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M27.62 18.03a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM24.5 21.84a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#6C3C31"
            />
            <path
              d="M32.23 18.04a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM35.7 16.6a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M34.75 21.28a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.84Z"
              fill="#31C9FF"
            />
            <path
              d="M26.14 25.26a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M33.84 28a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#6C3C31"
            />
            <path
              d="M30.84 25.12a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M34.96 33.52a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M34.39 37.27a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M40.94 26.24a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M36.72 29.88a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM30.42 21.28a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.84Z"
              fill="#F84FAA"
            />
            <path
              d="M30.45 31.5a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.83Z"
              fill="#6C3C31"
            />
            <path
              d="M30.42 34.89a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M36.72 24.93a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M39.58 18.96a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.84Z"
              fill="#F84FAA"
            />
            <path
              d="M40.5 22.76a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#6C3C31"
            />
            <path
              d="M35.25 44.92a.62.62 0 0 1-.53.06l-3.2-1.09a.62.62 0 0 1 .4-1.17l3.2 1.09a.62.62 0 0 1 .13 1.11ZM24.12 59.05a.62.62 0 0 0 .52-.71L24.1 55a.62.62 0 0 0-1.23.2l.53 3.33a.62.62 0 0 0 .71.52Zm11.3-5.37 2.25-2.53a.62.62 0 1 0-.93-.82l-2.24 2.53a.62.62 0 1 0 .93.82ZM28.2 50.5l.09-3.6a.62.62 0 0 0-1.25-.03l-.09 3.6c0 .34.27.62.61.63h.01c.34 0 .62-.26.63-.6Zm14.45 11.47v-3.6a.62.62 0 1 0-1.24 0v3.6a.62.62 0 1 0 1.24 0ZM32.9 60a.62.62 0 0 0 .05-.88l-2.25-2.51a.62.62 0 1 0-.93.83l2.25 2.51a.62.62 0 0 0 .88.05Zm7.93 7.37 3.12-.5a.62.62 0 0 0-.2-1.22l-3.11.49a.62.62 0 0 0 .2 1.23Zm5.87 4.46 1.6-2.73a.62.62 0 1 0-1.07-.62l-1.6 2.72a.62.62 0 1 0 1.07.63Zm-.22-16.27c.27-.21.31-.6.1-.88l-2.1-2.67a.62.62 0 0 0-.98.77l2.1 2.67a.62.62 0 0 0 .88.1ZM35.08 67.5l.34-3.58a.62.62 0 0 0-1.24-.12l-.34 3.58a.62.62 0 0 0 .62.68c.31 0 .59-.24.62-.56Z"
              fill="#FFFCE9"
            />
            <path
              d="M43.8 13.1a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#6C3C31"
            />
            <path
              d="M46.37 4.26a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.84Z"
              fill="#5822F3"
            />
            <path
              d="M42.33 4.92a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83ZM29.6 11.15a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M34.39 7.99a.92.92 0 1 0 0-1.84.92.92 0 0 0 0 1.84Z"
              fill="#6C3C31"
            />
            <path
              d="M38.62 7.33a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M25.42 15.03a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#5822F3"
            />
            <path
              d="M81.8 36.2c-.15-.98-.45-3.25-.48-5.6-.05-3.88.66-6.36 2-6.97a.7.7 0 0 0 .15-.1c-.18-.41-.36-.83-.56-1.24a.68.68 0 0 0-.18.06c-1.93.89-2.87 3.66-2.82 8.26.03 2.37.31 4.6.48 5.68-2.85.43-3.57 2.33-6.25 2.87 3.53-13.84 2.3-24.25 1.35-27.3a37.2 37.2 0 0 0-1.9-1.75l.1.22c.04.1 3.42 11.9-1.05 28.98h-.06c-2 .05-3.1-.68-3.1-.68.3-1.02 2.07-7.24 1.57-14.01-.13-1.76-.34-3.43-.54-5.06-.62-4.86-1.1-8.72 1-11.1-.38-.29-.76-.56-1.15-.82-2.42 2.86-1.88 7.15-1.25 12.1.2 1.6.4 3.25.53 4.98a41.4 41.4 0 0 1-1.41 13.14c-1.19-.8-2.31-1.66-4.67-1.66h-.13a13.5 13.5 0 0 1-11.87 14.98v.12c0 4.52-3.11 4.52-3.11 9.03 0 4.52 3.1 4.52 3.1 9.03 0 2.4-.87 3.53-1.7 4.73H50a36.1 36.1 0 0 0 25.7-10.64A36.1 36.1 0 0 0 86.34 37.9c-1.17-.8-2.27-1.65-4.54-1.7ZM53.84 24.79c.46.14.9.3 1.34.48a17.86 17.86 0 0 0 1.74-7.14c.07-2.6-.36-4.82-.74-6.78-.35-1.82-.68-3.54-.57-5.3.11-1.86.57-3.2.96-4.05-.48-.09-.96-.16-1.45-.23-.41.98-.8 2.36-.92 4.2-.12 1.93.23 3.74.6 5.65.38 1.98.78 4.02.71 6.48a17.04 17.04 0 0 1-1.67 6.69Z"
              fill="#6C3C31"
            />
            <path
              d="M62.84 17.68A113.7 113.7 0 0 1 61 29.9c.4.56.76 1.16 1.07 1.78.62-2.82 1.67-8.2 2.18-13.87.52-5.82-.99-11.39-1.97-14.28-.56-.2-1.13-.39-1.7-.56.85 2.3 2.82 8.43 2.26 14.71Z"
              fill="#6C3C31"
            />
            <path
              d="M22.99 18.43a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#F84FAA"
            />
            <path
              d="M28.82 28.49a.92.92 0 1 0 0-1.83.92.92 0 0 0 0 1.83Z"
              fill="#31C9FF"
            />
            <path
              d="M58.96 59.06a1.3 1.3 0 1 0 0-2.58 1.3 1.3 0 0 0 0 2.58ZM79.9 53.35a.79.79 0 1 0 0-1.58.79.79 0 0 0 0 1.58ZM71.54 49.77a.79.79 0 1 0 0-1.58.79.79 0 0 0 0 1.58ZM66.71 41.73a.79.79 0 1 0 0-1.57.79.79 0 0 0 0 1.57ZM55.22 55.2a1.13 1.13 0 1 0 0-2.27 1.13 1.13 0 0 0 0 2.26ZM76.94 44.17a.73.73 0 1 0 0-1.46.73.73 0 0 0 0 1.46ZM62.67 66.46a.82.82 0 1 0 0-1.64.82.82 0 0 0 0 1.64ZM57.67 70.98a.79.79 0 1 0 0-1.58.79.79 0 0 0 0 1.58ZM63.87 53.35a.79.79 0 1 0 0-1.58.79.79 0 0 0 0 1.58ZM72.13 57.28a1.29 1.29 0 1 0 0-2.58 1.29 1.29 0 0 0 0 2.58ZM65.92 47.44a.62.62 0 0 1-.44-1.06 6.3 6.3 0 0 1 3.32-1.64.62.62 0 1 1 .24 1.22 5.15 5.15 0 0 0-2.67 1.29.61.61 0 0 1-.45.19ZM69.2 64.81a.62.62 0 0 1-.63-.6 5.18 5.18 0 0 0-1.03-2.73.62.62 0 1 1 1.02-.72 6.35 6.35 0 0 1 1.26 3.4.62.62 0 0 1-.6.65h-.03ZM82.34 44.58a.62.62 0 0 1-.62-.61 5.17 5.17 0 0 0-.98-2.75.62.62 0 1 1 1.03-.7 6.34 6.34 0 0 1 1.2 3.43c0 .34-.28.62-.62.63ZM56.24 63.67a.63.63 0 0 1-.2-.03 5.55 5.55 0 0 1-3-2.54.62.62 0 1 1 1.09-.61 4.3 4.3 0 0 0 2.31 1.97.62.62 0 0 1-.2 1.21Z"
              fill="#B88980"
            />
            <path
              className={styles.wordmark}
              d="M1.13 76.69H0V75.6h1.13v-1.88h1.26v1.88h1.96v1.09H2.39v4.1c0 .48.1.82.27 1 .18.17.49.26.93.26h.77v1.1h-.94c-.77 0-1.34-.18-1.72-.55-.38-.36-.57-.98-.57-1.84v-4.07Zm6.42 2.46c0-.85.2-1.5.62-1.94.41-.45.96-.67 1.65-.67.66 0 1.18.2 1.57.6.38.4.58.98.58 1.74v4.27h1.25v-4.38c0-1.07-.27-1.88-.81-2.45a2.82 2.82 0 0 0-2.13-.84c-.65 0-1.21.16-1.7.5-.5.32-.84.8-1.03 1.4v-4.4H6.3v10.17h1.25v-4Zm10.2 12.4a10.5 10.5 0 0 1 1.25 5.2c0 1.95-.41 3.69-1.25 5.2a8.8 8.8 0 0 1-3.62 3.55 11.53 11.53 0 0 1-5.54 1.27H1.05V86.8h7.54c2.14 0 4 .41 5.56 1.24 1.56.83 2.76 2 3.6 3.5Zm-3.63 5.2c0-1.81-.52-3.21-1.57-4.21-1.04-1-2.5-1.5-4.39-1.5H5.88v11.4h2.28c1.88 0 3.35-.49 4.4-1.49 1.04-1 1.56-2.4 1.56-4.2Zm25.78-5.3c.88 1.55 1.32 3.3 1.32 5.27 0 1.95-.44 3.71-1.34 5.28a9.62 9.62 0 0 1-3.64 3.65 10.1 10.1 0 0 1-5.1 1.31 10.1 10.1 0 0 1-5.1-1.3A9.64 9.64 0 0 1 22.4 102a10.44 10.44 0 0 1-1.34-5.28c0-1.96.45-3.72 1.34-5.27a9.67 9.67 0 0 1 3.65-3.64 10.1 10.1 0 0 1 5.1-1.32c1.88 0 3.58.44 5.12 1.32a9.58 9.58 0 0 1 3.63 3.64Zm-3.59 5.27c0-1.75-.47-3.15-1.4-4.18a4.82 4.82 0 0 0-3.77-1.56c-1.58 0-2.83.52-3.77 1.54a6 6 0 0 0-1.4 4.2 6 6 0 0 0 1.4 4.17 4.82 4.82 0 0 0 3.77 1.56c1.56 0 2.82-.52 3.76-1.56a5.97 5.97 0 0 0 1.41-4.17Zm20.44.4-12.9-11.14v20.78h4.82v-10.2l12.9 11.03V86.8h-4.82v10.3Zm20.02 1.2c0 1.32-.3 2.33-.9 3.02-.59.7-1.45 1.05-2.58 1.05-1.13 0-1.99-.35-2.58-1.05-.6-.7-.89-1.7-.89-3.02V86.81h-4.8v11.51c0 1.89.35 3.48 1.05 4.78a6.8 6.8 0 0 0 2.87 2.9 9 9 0 0 0 4.24.96c1.6 0 3.03-.32 4.3-.97a7.15 7.15 0 0 0 3.01-2.9 9.5 9.5 0 0 0 1.1-4.77V86.81h-4.83v11.51Zm7.59-11.51v3.84h5.36v16.11h4.86V90.66H100V86.8H84.36Zm-64.13-3.9c-.5.22-1.07.34-1.7.34a3.9 3.9 0 0 1-1.93-.47 3.3 3.3 0 0 1-1.31-1.34 4.24 4.24 0 0 1-.48-2.06c0-.78.16-1.47.48-2.06.31-.59.75-1.03 1.31-1.35a3.9 3.9 0 0 1 1.93-.46c.73 0 1.37.15 1.92.46.54.32.96.73 1.25 1.26.29.52.43 1.1.43 1.73 0 .26-.02.5-.05.69h-6.04c.03.86.27 1.5.73 1.94a2.4 2.4 0 0 0 1.7.64c.61 0 1.12-.15 1.52-.46.37-.28.6-.64.71-1.09h1.34a3.17 3.17 0 0 1-1.8 2.23Zm-4.18-4.06h4.82c0-.46-.1-.89-.28-1.21-.2-.38-.5-.66-.86-.84a2.64 2.64 0 0 0-1.2-.27c-.69 0-1.27.2-1.73.64-.44.39-.68.95-.75 1.68Z"
              fill="#fff"
              style={letterFillStyle}
            />
          </svg>
        )
      case "emblem":
        return (
          <svg
            className={[styles.svg, styles.vertical].join(" ")}
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.dough}
              d="M5 56.1c.3.2.4.4.5.7.1.7-.7 1.7-.8 1.9a1 1 0 0 1-.8.3c-.4 0-.8-.1-1.2-.4-.6-.5-.8-1.2-.4-1.8.6-.7 1.9-1.2 2.8-.7ZM2.2 42.5H2.7c.3 0 .8-.1 1-.7 0-.6 0-1.7-.8-2.2-.4-.2-.9-.1-1.3.2-.5.6-.7 1.6-.4 2.2 0 .2.4.5 1 .5ZM6.5 76c-.6-.3-1.4-.5-2.2-.5-.6 0-2.8.1-3 1.6-.3 1.1.4 2.6 1.3 3.3.4.3.9.5 1.3.5.3 0 .6 0 .8-.2 1.1-.6 3-1.8 2.8-3.3 0-.5-.4-1-1-1.4Zm108.8-15.8A55 55 0 0 1 60 115.2a55.6 55.6 0 0 1-49.3-30c-.2-.4-.1-.9.2-1.1 3.6-3.2 5.7-8 5.5-12.8v-.1l.1-.1a18.9 18.9 0 0 0-1.4-29.9H15V41c0-3.1-.7-6.2-2.3-8.8a.8.8 0 0 1 0-.9A55.9 55.9 0 0 1 60 4.7a56 56 0 0 1 24.9 6A55.2 55.2 0 0 1 115.3 60ZM60 77.7c1 0 1.8 0 2.7-.2a17.6 17.6 0 0 0 13.4-24.7 17.6 17.6 0 0 0-27.4-6.4 18 18 0 0 0-5.3 19.8A17.8 17.8 0 0 0 60 77.7Zm-48.8-7.4c.7 0 1.3-.3 1.6-.8.7-1 .4-2.8 0-4-.2-.6-.6-.8-.9-.8-.5-.1-1 .1-1.5.6-.8.8-1.1 2-1 3 .1 1 .7 2 1.8 2ZM10 48.8c0-.6 0-3.2-1.5-3.4-1.2-.2-2.2 1-2.6 1.9-.6 1.2-.5 2.1.2 2.6.4.3 1 .5 1.7.5.4 0 .9 0 1.2-.3.6-.2 1-.7 1-1.3Z"
              fill="#EBBC60"
              style={doughFillStyle}
            />
            <path
              d="M24.3 99.4a52.8 52.8 0 0 0 35.5 13.8c1.2-1.8 2.5-3.4 2.5-7 0-6.6-4.6-6.6-4.6-13.2s4.7-6.6 4.6-13.4l-2.3.2a19.8 19.8 0 0 1-19.7-17.5h-.1c-6 0-6.6-3.8-11.7-4.5a23 23 0 0 1-7.6 15s0 7.7-7 12.8c-.2.2-.3.5-.1.8a53.4 53.4 0 0 0 10.5 13Z"
              fill="#F84FAA"
            />
            <path
              d="M40.2 60c0-10.2 7.7-18.5 17.5-19.7v-.1c0-6.6 4.6-6.6 4.6-13.2 0-6.7-4.6-6.7-4.6-13.3 0-3.5 1.3-5.1 2.5-6.9A52.9 52.9 0 0 0 16 30.2c-.1.2-.1.5 0 .8 1.3 1.3 3.8 4.6 4 8.7 0 0 8.4 7.1 8.7 15.4v2.7c5 .7 5.5 4.5 11.7 4.5V60Z"
              fill="#FFFCE9"
            />
            <path
              d="M97.6 22.4A52.8 52.8 0 0 0 60.2 6.8c-1.2 1.8-2.5 3.4-2.5 7 0 6.5 4.6 6.5 4.6 13.2 0 6.6-4.6 6.6-4.6 13.2v.1l2.3-.1c10.2 0 18.5 7.7 19.7 17.5h.1c6.6 0 6.6 4.7 13.3 4.7 6.6 0 6.6-4.7 13.2-4.7 3.5 0 5.1 1.3 6.9 2.5V60c0-14.2-5.5-27.6-15.6-37.6ZM55.4 21.1a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#31C9FF"
            />
            <path
              d="M58 26.2a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7ZM45.3 27a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.6ZM53.4 16a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M50.7 29.6a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#31C9FF"
            />
            <path
              d="M54 39.1a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M57.2 32.6a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M52 34.6a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M40.6 22.4a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#31C9FF"
            />
            <path
              d="M48.1 19.5a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M34.9 23.2a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M29.9 26.2a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M27.3 31.1a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6ZM22.7 36.7a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#6C3C31"
            />
            <path
              d="M34 31.1a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6ZM39 29a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z"
              fill="#F84FAA"
            />
            <path
              d="M37.7 35.9a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#31C9FF"
            />
            <path
              d="M25 41.7a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M36.3 45.7a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#6C3C31"
            />
            <path
              d="M32 41.5a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M38 53.8a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M37.2 59.3a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M46.7 43.1a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z"
              fill="#31C9FF"
            />
            <path
              d="M40.6 48.5a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7ZM31.3 35.9a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M31.4 50.8a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#6C3C31"
            />
            <path
              d="M31.3 55.8a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M40.6 41.2a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M44.8 32.5a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M46 38a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z"
              fill="#6C3C31"
            />
            <path
              d="M38.4 70.5a1 1 0 0 1-.8 0L33 69a1 1 0 0 1 .5-1.7l4.7 1.5a1 1 0 0 1 .2 1.7ZM22.1 91.2c.5-.1.9-.6.8-1l-.8-5a1 1 0 0 0-1.8.3l.8 4.9a1 1 0 0 0 1 .8Zm16.6-7.9 3.3-3.7a1 1 0 1 0-1.4-1.2L37.3 82a1 1 0 1 0 1.4 1.2ZM28 78.6l.1-5.2a1 1 0 0 0-1.8 0l-.1 5.2c0 .5.4 1 .9 1a1 1 0 0 0 .9-1Zm21.1 16.8v-5.2c0-.5-.4-1-.9-1a1 1 0 0 0-.9 1v5.2c0 .5.4 1 1 1 .4 0 .8-.5.8-1ZM35 92.6c.4-.4.4-1 0-1.3l-3.2-3.7a1 1 0 1 0-1.4 1.2l3.3 3.7a1 1 0 0 0 1.3 0Zm11.6 10.7 4.6-.7a1 1 0 0 0-.3-1.8l-4.6.7a1 1 0 0 0 .3 1.8Zm8.6 6.6 2.3-4a1 1 0 1 0-1.6-1l-2.3 4a1 1 0 1 0 1.6 1ZM54.8 86c.4-.3.5-1 .2-1.3l-3-4a1 1 0 0 0-1.5 1.2l3 4a.9.9 0 0 0 1.3 0Zm-16.6 17.4.5-5.2c0-.5-.4-1-.9-1a1 1 0 0 0-1 .8l-.4 5.2c-.1.5.3 1 .8 1a1 1 0 0 0 1-.8Z"
              fill="#FFFCE9"
            />
            <path
              d="M51 24a1.3 1.3 0 1 0 0-2.8 1.3 1.3 0 0 0 0 2.7Z"
              fill="#6C3C31"
            />
            <path
              d="M54.7 11a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#5822F3"
            />
            <path
              d="M48.8 12a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.6ZM30.2 21a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.7Z"
              fill="#31C9FF"
            />
            <path
              d="M37.2 16.4a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#6C3C31"
            />
            <path
              d="M43.3 15.5a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M24 26.7a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z"
              fill="#5822F3"
            />
            <path
              d="M106.5 57.7a63 63 0 0 1-.7-8.2c0-5.7 1-9.3 3-10.2l.2-.1-.8-1.8a1 1 0 0 0-.3 0c-2.8 1.3-4.2 5.4-4.1 12.1 0 3.5.4 6.8.7 8.3-4.2.7-5.2 3.5-9.2 4.3a85.4 85.4 0 0 0 2-40l-2.8-2.6.2.4c0 .1 5 17.4-1.6 42.4-3 0-4.6-1-4.6-1 .5-1.5 3-10.6 2.3-20.5l-.8-7.4c-.9-7.2-1.6-12.8 1.5-16.3a54 54 0 0 0-1.7-1.2C86.3 20.1 87 26.4 88 33.6l.8 7.3c.6 8.6-1.5 16.8-2.1 19.3-1.8-1.2-3.4-2.5-6.9-2.5h-.1a19.8 19.8 0 0 1-17.4 22v.1c0 6.6-4.6 6.6-4.6 13.2s4.6 6.6 4.6 13.2c0 3.6-1.3 5.2-2.5 7h.2a52.8 52.8 0 0 0 53.2-53c-1.7-1.2-3.3-2.4-6.7-2.5ZM65.6 41l2 .7c.9-1.8 2.4-5.6 2.5-10.4.1-3.8-.5-7-1-10-.6-2.6-1-5.1-.9-7.7.2-2.7.8-4.7 1.4-6l-2.1-.3c-.6 1.5-1.2 3.5-1.4 6.2-.1 2.8.4 5.4 1 8.2.5 3 1 6 1 9.5a25 25 0 0 1-2.5 9.8Z"
              fill="#6C3C31"
            />
            <path
              d="M78.8 30.6c-.6 6.9-1.8 13.5-2.7 17.9l1.6 2.6c.9-4.1 2.4-12 3.2-20.3.7-8.5-1.5-16.7-3-20.9a51 51 0 0 0-2.4-.8 52.7 52.7 0 0 1 3.3 21.5Z"
              fill="#6C3C31"
            />
            <path
              d="M20.5 31.7a1.3 1.3 0 1 0 0-2.7 1.3 1.3 0 0 0 0 2.7Z"
              fill="#F84FAA"
            />
            <path
              d="M29 46.4a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z"
              fill="#31C9FF"
            />
            <path
              d="M73.1 91.2a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8ZM103.8 82.8a1.2 1.2 0 1 0 0-2.3 1.2 1.2 0 0 0 0 2.3ZM91.5 77.6a1.2 1.2 0 1 0 0-2.3 1.2 1.2 0 0 0 0 2.3ZM84.5 65.8a1.2 1.2 0 1 0 0-2.3 1.2 1.2 0 0 0 0 2.3ZM67.6 85.5a1.7 1.7 0 1 0 0-3.3 1.7 1.7 0 0 0 0 3.3ZM99.4 69.4a1 1 0 1 0 0-2.2 1 1 0 0 0 0 2.2ZM78.5 102a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4ZM71.2 108.6a1.2 1.2 0 1 0 0-2.3 1.2 1.2 0 0 0 0 2.3ZM80.3 82.8a1.2 1.2 0 1 0 0-2.3 1.2 1.2 0 0 0 0 2.3ZM92.4 88.6a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8ZM83.3 74.2a1 1 0 0 1-.6-1.6c1-1.1 2.8-2 4.8-2.4A1 1 0 1 1 88 72c-1.7.3-3 1-4 1.9-.1.2-.4.3-.6.3ZM88 99.6a1 1 0 0 1-.8-.9c0-1.3-.6-2.7-1.5-4a1 1 0 1 1 1.5-1 9.3 9.3 0 0 1 1.8 5c0 .5-.4.9-.9.9ZM107.3 70a1 1 0 0 1-.9-1c0-1.2-.5-2.6-1.4-4a1 1 0 1 1 1.5-1 9.3 9.3 0 0 1 1.7 5c0 .6-.4 1-.9 1ZM69.1 98a1 1 0 0 1-.2-.1c-1.8-.6-3.4-2-4.4-3.8a1 1 0 1 1 1.5-.8c.8 1.3 2 2.4 3.4 2.8a1 1 0 0 1-.3 1.8Z"
              fill="#B88980"
            />
          </svg>
        )

      default:
        return (
          <svg
            className={styles.svg}
            viewBox="0 0 255 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.dough}
              d="M3 37.1l.2.5c.1.5-.4 1.2-.6 1.4-.1.2-.3.2-.6.2-.2 0-.5 0-.8-.3-.4-.3-.6-.9-.3-1.3.4-.5 1.4-.9 2-.5zM.7 27.3h.4c.2 0 .6-.1.7-.5 0-.5 0-1.3-.5-1.6-.3-.2-.7-.1-1 .1-.4.4-.5 1.2-.3 1.6 0 .2.3.4.7.4zm3.1 24.2c-.4-.3-1-.4-1.5-.4s-2 0-2.3 1.2c-.1.7.4 1.8 1 2.4l1 .3c.2 0 .4 0 .6-.2.8-.4 2-1.3 2-2.3 0-.4-.3-.7-.8-1zM82.7 40a39.8 39.8 0 01-40 40A40.3 40.3 0 017.1 58c-.2-.2-.1-.5.1-.7a12 12 0 004-9.3l.1-.1a13.7 13.7 0 00-1-21.7h-.1v-.1a12 12 0 00-1.7-6.4.6.6 0 010-.6A40 40 0 0182.7 40zm-40 12.8c.7 0 1.3 0 2-.2a12.8 12.8 0 009.7-18 12.8 12.8 0 00-19.9-4.5 13 13 0 00-3.8 14.3c1.9 5 6.7 8.4 12 8.4zM7.4 47.4c.5 0 .9-.3 1.1-.6.5-.7.3-2 0-2.8-.1-.5-.4-.6-.6-.7-.4 0-.8.1-1.1.5-.6.6-.8 1.4-.7 2.2 0 .7.4 1.4 1.2 1.4zm-1-15.6c0-.4 0-2.3-1-2.5-1 0-1.7.8-2 1.4-.4 1-.3 1.6.2 2 .3.2.8.3 1.2.3.3 0 .7 0 1-.2.3-.2.6-.6.6-1z"
              fill="#EBBC60"
              style={doughFillStyle}
            />
            <path
              d="M16.9 68.5c7 6.4 16 9.9 25.6 10 1-1.3 1.8-2.5 1.8-5 0-4.9-3.2-4.9-3.2-9.6 0-4.9 3.3-4.8 3.2-9.7a14.3 14.3 0 01-15.8-12.6h-.2c-4.3 0-4.7-2.8-8.4-3.3-.4 5-2.9 8.4-5.5 11 0 0 0 5.5-5 9.2-.2 0-.3.3-.2.5a38.7 38.7 0 007.7 9.5z"
              fill="#F84FAA"
            />
            <path
              d="M28.4 40c0-7.4 5.5-13.5 12.7-14.3v-.1c0-4.8 3.2-4.8 3.2-9.6s-3.2-4.8-3.2-9.6c0-2.5.9-3.7 1.8-5a38.3 38.3 0 00-32.1 17v.5a11 11 0 012.9 6.3s6 5.2 6.3 11.2l-.1 2c3.7.4 4 3.2 8.6 3.2l-.1-1.7z"
              fill="#FFFCE9"
            />
            <path
              d="M70 12.7A38.2 38.2 0 0042.8 1.4c-1 1.3-1.8 2.5-1.8 5 0 4.8 3.2 4.8 3.2 9.6s-3.2 4.8-3.2 9.6v.1l1.6-.1c7.3 0 13.4 5.5 14.2 12.7h.2c4.7 0 4.7 3.4 9.5 3.4s4.8-3.4 9.6-3.4c2.5 0 3.7 1 5 1.8v-.2c0-10.3-4-20-11.3-27.2zM39.4 11.8a1 1 0 100-2 1 1 0 000 2z"
              fill="#31C9FF"
            />
            <path
              d="M41.3 15.5a1 1 0 100-2 1 1 0 000 2zM32 16a1 1 0 100-2 1 1 0 000 2zM37.9 8a1 1 0 100-1.9 1 1 0 000 2z"
              fill="#F84FAA"
            />
            <path d="M36 17.9a1 1 0 100-2 1 1 0 000 2z" fill="#31C9FF" />
            <path d="M38.4 24.8a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M40.7 20a1 1 0 100-1.8 1 1 0 000 1.9z" fill="#5822F3" />
            <path d="M36.8 21.6a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M28.6 12.7a1 1 0 100-2 1 1 0 000 2z" fill="#31C9FF" />
            <path d="M34 10.6a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M24.5 13.3a1 1 0 100-2 1 1 0 000 2z" fill="#5822F3" />
            <path d="M20.9 15.5a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path
              d="M19 19a1 1 0 100-1.9 1 1 0 000 2zM15.7 23a1 1 0 100-1.9 1 1 0 000 2z"
              fill="#6C3C31"
            />
            <path
              d="M23.9 19a1 1 0 100-1.9 1 1 0 000 2zM27.5 17.5a1 1 0 100-2 1 1 0 000 2z"
              fill="#F84FAA"
            />
            <path d="M26.5 22.5a1 1 0 100-2 1 1 0 000 2z" fill="#31C9FF" />
            <path d="M17.4 26.7a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M25.6 29.6a1 1 0 100-2 1 1 0 000 2z" fill="#6C3C31" />
            <path d="M22.4 26.5a1 1 0 100-1.9 1 1 0 000 2z" fill="#5822F3" />
            <path d="M26.8 35.4a1 1 0 100-1.9 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M26.2 39.4a1 1 0 100-2 1 1 0 000 2z" fill="#5822F3" />
            <path d="M33.1 27.7a1 1 0 100-2 1 1 0 000 2z" fill="#31C9FF" />
            <path
              d="M28.6 31.6a1 1 0 100-2 1 1 0 000 2zM22 22.5a1 1 0 100-2 1 1 0 000 2z"
              fill="#F84FAA"
            />
            <path d="M22 33.3a1 1 0 100-2 1 1 0 000 2z" fill="#6C3C31" />
            <path d="M22 36.9a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M28.6 26.3a1 1 0 100-1.9 1 1 0 000 2z" fill="#5822F3" />
            <path d="M31.7 20a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M32.6 24a1 1 0 100-1.9 1 1 0 000 2z" fill="#6C3C31" />
            <path
              d="M27 47.5c-.1.1-.3.2-.5 0l-3.4-1a.7.7 0 11.4-1.3l3.4 1.1a.7.7 0 01.2 1.2zm-11.7 15c.3 0 .6-.4.5-.8l-.5-3.5a.7.7 0 10-1.3.2l.5 3.5a.7.7 0 00.8.6zm12-5.7l2.3-2.7a.7.7 0 10-1-.9L26.3 56a.7.7 0 101 .9zm-7.7-3.4v-3.8a.7.7 0 00-1.2 0l-.1 3.8c0 .4.3.7.6.7.4 0 .7-.3.7-.7zm15.3 12.2v-3.8a.7.7 0 10-1.3 0v3.8a.7.7 0 101.3 0zm-10.3-2c.3-.3.3-.8 0-1l-2.4-2.7a.7.7 0 00-.9 0c-.3.2-.3.6 0 .9l2.4 2.7a.7.7 0 00.9 0zm8.4 7.7l3.3-.5a.7.7 0 10-.2-1.3l-3.3.5a.7.7 0 000 1.3h.2zm6.2 4.7l1.7-2.8a.7.7 0 00-1.1-.7L38 75.4a.7.7 0 001.1.6zM39 58.8c.3-.2.3-.6 0-1L36.9 55a.7.7 0 10-1 .8l2.2 2.9a.7.7 0 001 .1zM26.9 71.4l.3-3.8a.7.7 0 10-1.3 0l-.3 3.7c0 .4.2.7.6.7.4 0 .7-.2.7-.6z"
              fill="#FFFCE9"
            />
            <path d="M36.1 13.8a1 1 0 100-2 1 1 0 000 2z" fill="#6C3C31" />
            <path d="M38.9 4.4a1 1 0 100-2 1 1 0 000 2z" fill="#5822F3" />
            <path
              d="M34.6 5.1a1 1 0 100-1.9 1 1 0 000 2zM21 11.7a1 1 0 100-1.9 1 1 0 000 2z"
              fill="#31C9FF"
            />
            <path d="M26.2 8.4a1 1 0 100-2 1 1 0 000 2z" fill="#6C3C31" />
            <path d="M30.6 7.7a1 1 0 100-2 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M16.7 15.8a1 1 0 100-1.9 1 1 0 000 2z" fill="#5822F3" />
            <path
              d="M76.4 38.3c-.2-1-.5-3.5-.5-6 0-4 .7-6.7 2.1-7.3l.2-.1-.6-1.4-.2.1c-2 1-3 3.9-3 8.8 0 2.5.3 4.8.5 6-3 .4-3.8 2.4-6.6 3a61.8 61.8 0 001.4-29 39 39 0 00-2-1.8l.1.3a62.4 62.4 0 01-1.2 30.7 6 6 0 01-3.3-.7c.4-1.1 2.2-7.7 1.7-14.9-.1-1.9-.4-3.6-.6-5.3-.6-5.2-1.1-9.3 1-11.8a42 42 0 00-1.1-.9c-2.6 3-2 7.6-1.4 12.8.2 1.7.5 3.5.6 5.3.5 6.2-1 12.2-1.5 14-1.2-1-2.4-1.8-5-1.8v1.6c0 7.4-5.5 13.4-12.7 14.3 0 4.9-3.3 4.9-3.3 9.7 0 4.7 3.3 4.7 3.3 9.5 0 2.6-.9 3.8-1.8 5h.2a38.3 38.3 0 0038.5-38.3c-1.2-.9-2.4-1.8-4.8-1.8zM46.8 26.2l1.4.5a19 19 0 001.8-7.6c.1-2.7-.4-5-.8-7.2-.3-1.9-.7-3.7-.6-5.6.2-2 .6-3.4 1-4.3l-1.5-.2c-.4 1-.8 2.5-1 4.4 0 2.1.3 4 .7 6 .4 2.1.8 4.3.7 6.9 0 3.3-1.2 6-1.7 7z"
              fill="#6C3C31"
            />
            <path
              d="M56.3 18.7c-.4 5-1.3 9.8-2 13 .5.5.9 1.1 1.2 1.8.6-3 1.8-8.7 2.3-14.7.5-6.2-1-12-2-15.1L53.8 3c1 2.4 3 8.9 2.4 15.6z"
              fill="#6C3C31"
            />
            <path d="M14 19.4a1 1 0 100-1.9 1 1 0 000 2z" fill="#F84FAA" />
            <path d="M20.3 30.1a1 1 0 100-2 1 1 0 000 2z" fill="#31C9FF" />
            <path
              d="M52.2 62.5a1.4 1.4 0 100-2.7 1.4 1.4 0 000 2.7zM74.4 56.4a.8.8 0 100-1.6.8.8 0 000 1.6zM65.5 52.7a.8.8 0 100-1.7.8.8 0 000 1.7zM60.4 44.1a.8.8 0 100-1.6.8.8 0 000 1.6zM48.2 58.4a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4zM71.2 46.7a.8.8 0 100-1.5.8.8 0 000 1.5zM56.1 70.3a.9.9 0 100-1.7.9.9 0 000 1.7zM50.8 75.1a.8.8 0 100-1.6.8.8 0 000 1.6zM57.4 56.4a.8.8 0 100-1.6.8.8 0 000 1.6zM66.1 60.6a1.4 1.4 0 100-2.7 1.4 1.4 0 000 2.7zM59.6 50.2A.7.7 0 0159 49c.8-.9 2-1.5 3.5-1.8a.7.7 0 11.3 1.3c-1.2.3-2.2.7-2.9 1.4l-.4.2zM63 68.6a.7.7 0 01-.6-.6c0-1-.4-2-1.1-3a.7.7 0 111-.7 6.7 6.7 0 011.4 3.6c0 .4-.3.7-.6.7zM77 47.2a.7.7 0 01-.7-.7c0-.9-.4-2-1-2.9a.7.7 0 011-.7 6.7 6.7 0 011.3 3.6c0 .4-.3.7-.6.7zM49.3 67.4h-.2a5.9 5.9 0 01-3.2-2.7.7.7 0 111.2-.7c.5 1 1.4 1.8 2.4 2.1a.7.7 0 01-.2 1.3z"
              fill="#B88980"
            />
            <path
              className={styles.wordmark}
              d="M97.7 26.4v1.9H96c-1.2 0-2.2-.3-2.8-1-.6-.5-1-1.6-1-3v-6.6h-1.8v-1.8h1.9v-3.1h2v3h3.2v1.9h-3.2v6.7c0 .8.2 1.3.5 1.6.3.3.8.4 1.5.4h1.3zm13.1-9.3c1 .9 1.4 2.2 1.4 4v7.2h-2v-7c0-1.3-.4-2.2-1-2.9-.7-.7-1.5-1-2.6-1s-2 .4-2.7 1.1c-.7.7-1 1.8-1 3.2v6.5h-2V11.6h2v7.2c.3-1 .9-1.8 1.7-2.3.8-.5 1.7-.8 2.7-.8 1.5 0 2.6.4 3.5 1.4zm8.8 42a14.4 14.4 0 01-6 5.7c-2.5 1.4-5.5 2.1-9 2.1H92.2V34.2h12.4c3.5 0 6.5.7 9 2 2.6 1.4 4.6 3.3 6 5.8 1.4 2.5 2 5.3 2 8.5s-.6 6-2 8.5zm-8.5-1.7a9 9 0 002.5-6.9 9 9 0 00-2.5-6.9 10 10 0 00-7.2-2.4H100v18.6h3.8c3 0 5.5-.8 7.2-2.4zm38.8-21.5c2.5 1.4 4.5 3.4 6 6 1.4 2.5 2.1 5.4 2.1 8.6 0 3.2-.7 6-2.2 8.6a15.8 15.8 0 01-6 6 16.6 16.6 0 01-8.3 2.1 15.8 15.8 0 01-14.3-8.1 17 17 0 01-2.2-8.6c0-3.2.7-6.1 2.2-8.7 1.5-2.5 3.5-4.5 6-6 2.5-1.4 5.3-2 8.3-2 3.1 0 5.9.6 8.4 2zm-14.6 7.7a9.8 9.8 0 00-2.2 6.9c0 2.8.7 5.1 2.2 6.8a7.9 7.9 0 006.2 2.5 8 8 0 006.2-2.5c1.5-1.7 2.3-4 2.3-6.8 0-3-.8-5.2-2.3-6.9a7.9 7.9 0 00-6.2-2.5 8 8 0 00-6.2 2.5zm56 24.7l-21-18v16.6h-8V33L183.5 51V34.2h7.9v34zm13.6-34V53c0 2.2.4 3.8 1.4 5 1 1 2.4 1.7 4.3 1.7 1.8 0 3.2-.6 4.2-1.8 1-1 1.4-2.7 1.4-4.9V34.2h8v18.9c0 3-.7 5.7-1.9 7.8a11.7 11.7 0 01-4.9 4.8c-2 1-4.4 1.5-7 1.5-2.7 0-5-.5-7-1.5s-3.6-2.7-4.7-4.8a16.4 16.4 0 01-1.7-7.8V34.2h7.9zm49.4 0v6.2h-8.9V67h-8V40.5h-8.7v-6.3h25.6zM124.4 24.1c-.2.7-.5 1.3-1.1 1.8a4 4 0 01-2.5.7 4 4 0 01-2.8-1c-.8-.7-1.2-1.8-1.2-3.2h9.9a6.2 6.2 0 00-.7-4 5.3 5.3 0 00-2-2c-.9-.5-2-.8-3.1-.8-1.2 0-2.3.3-3.2.8-1 .5-1.6 1.2-2.2 2.2a7 7 0 00-.7 3.4 7 7 0 00.7 3.3c.6 1 1.3 1.7 2.2 2.2 1 .6 2 .8 3.2.8 1 0 2-.2 2.7-.6a5.2 5.2 0 003-3.6h-2.2zm-6.4-5.8a4 4 0 012.9-1c.7 0 1.3.1 2 .4.5.3 1 .8 1.3 1.4.3.5.5 1.3.5 2h-7.9a4 4 0 011.2-2.8z"
              fill="#fff"
              style={letterFillStyle}
            />
          </svg>
        )
    }
  }

  return (
    <div
      className={[styles.wrapper, className].join(" ")}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick && "button"}
      tabIndex={onClick && 0}
      style={onClick && { cursor: "pointer" }}
    >
      <ConditionalWrapper
        condition={!!linkTo && !onClick}
        wrapper={children => (
          <Link to={linkTo!} className={styles.link} title="The DONUT Logo">
            {children}
          </Link>
        )}
      >
        {renderSwitch()}
      </ConditionalWrapper>
    </div>
  )
}

export default Logo
